import { AnimatePresence, motion } from 'framer-motion';
import { Award, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useActions, useAppData } from '../data';
import {
  ACHIEVEMENTS_BY_ID,
  TIER_COLOR,
} from '../lib/achievements/registry';
import { springGentle } from '../tokens';

const VISIBLE_MS = 3500;

/**
 * Top-of-screen toast that surfaces a single newly-unlocked achievement
 * at a time. Reads from `profile.achievements.unlocks` and shows the
 * first entry where `seen === false`. Dismissing marks it seen, which
 * naturally promotes the next unseen unlock.
 *
 * Honors `achievementsEnabled` — disabled users never see the toast,
 * but unlocks are still recorded so toggling back on doesn't lose
 * history.
 */
export function AchievementToast() {
  const data = useAppData();
  const actions = useActions();
  const [activeId, setActiveId] = useState<string | null>(null);
  const dismissTimerRef = useRef<number | null>(null);

  const enabled = data.profile.achievements?.achievementsEnabled !== false;
  const nextUnseen = useMemo(() => {
    if (!enabled) return null;
    return data.profile.achievements?.unlocks.find((u) => !u.seen) ?? null;
  }, [enabled, data.profile.achievements?.unlocks]);

  // Surface the next unseen unlock when nothing is currently showing.
  useEffect(() => {
    if (!nextUnseen) {
      setActiveId(null);
      return;
    }
    if (activeId) return;
    setActiveId(nextUnseen.id);
  }, [nextUnseen, activeId]);

  // Auto-dismiss timer. Re-arms whenever activeId changes.
  useEffect(() => {
    if (!activeId) return;
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = window.setTimeout(() => {
      actions.dismissAchievementUnlock(activeId);
      setActiveId(null);
    }, VISIBLE_MS);
    return () => {
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [activeId, actions]);

  const def = activeId ? ACHIEVEMENTS_BY_ID.get(activeId) : null;

  const close = () => {
    if (!activeId) return;
    actions.dismissAchievementUnlock(activeId);
    setActiveId(null);
  };

  return (
    <AnimatePresence>
      {def && activeId ? (
        <motion.div
          key={activeId}
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="fixed left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 max-w-[90vw]"
          style={{
            top: 'calc(env(safe-area-inset-top) + 12px)',
          }}
          role="status"
          aria-live="polite"
        >
          <div className="inline-flex items-center gap-3 rounded-full bg-text-primary text-bg px-4 py-3 shadow-lg">
            <span
              className="w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0"
              style={{ backgroundColor: TIER_COLOR[def.tier] }}
            >
              <Award size={14} strokeWidth={2} className="text-white" />
            </span>
            <span className="flex flex-col items-start min-w-0">
              <span className="text-[11px] font-medium text-text-tertiary">
                Achievement unlocked
              </span>
              <span className="text-[14px] font-semibold truncate max-w-[60vw]">
                {def.title}
              </span>
            </span>
            <button
              type="button"
              onClick={close}
              aria-label="Dismiss"
              className="ml-1 w-6 h-6 inline-flex items-center justify-center rounded-full opacity-70"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

// Suppress an unused-import warning when no exit animation is in flight.
void springGentle;
